import { Link } from 'react-router-dom'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  primary:
    'border-primary bg-primary text-white shadow-cta hover:bg-primary-hover hover:border-primary-hover',
  secondary:
    'border-line bg-white/80 text-muted backdrop-blur hover:border-edge-soft hover:bg-white hover:text-ink',
  ghost: 'border-transparent bg-transparent text-muted hover:bg-surface hover:text-ink',
}

const SIZES: Record<Size, string> = {
  // 44px min target keeps every control comfortable on touch.
  md: 'min-h-[44px] px-4 py-2 text-[12.5px]',
  lg: 'min-h-[50px] px-6 py-3 text-[13.5px]',
}

/**
 * Hover scales the control about its own centre rather than translating it.
 * A control that moves toward the cursor is harder to hit, not easier — it
 * shifts out from under a click that is already in flight.
 */
const BASE =
  'group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-control ' +
  'border font-bold transition-[background-color,border-color,color,box-shadow,transform] ' +
  'duration-300 ease-brand disabled:cursor-not-allowed disabled:opacity-60 ' +
  'motion-safe:hover:scale-[1.035] motion-safe:active:scale-[0.985]'

/** Sheen that sweeps across the control on hover. */
function Sheen() {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-brand group-hover:translate-x-full motion-reduce:hidden"
    />
  )
}

export interface ButtonBaseProps {
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
  /** Adds the animated gradient halo used on the primary calls to action. */
  glow?: boolean
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  children,
  glow = false,
  ...rest
}: ButtonBaseProps & ComponentPropsWithoutRef<'button'>) {
  return (
    <span className={cn('relative inline-flex', glow ? 'btn-glow' : undefined)}>
      <button className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
        <Sheen />
        <span className="relative">{children}</span>
      </button>
    </span>
  )
}

export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  className,
  children,
  glow = false,
  hard = false,
  ...rest
}: ButtonBaseProps & { to: string; hard?: boolean } & Omit<
    ComponentPropsWithoutRef<'a'>,
    'href'
  >) {
  // `hard` — a real anchor and a full page load, for a same-origin path this SPA does not
  // route. The product itself (/login, /dashboard) is a separate Next.js application served
  // from the same domain; react-router would try to resolve /login against its own route table,
  // find nothing, and render the 404 page. It would look like a broken link and be a routing
  // assumption. `external` stays for off-site links, which additionally open in a new tab —
  // wrong for the terminal, where the customer is entering the product, not leaving for it.
  const offsite = /^https?:/.test(to)
  const anchor = offsite || hard

  const inner = (
    <>
      <Sheen />
      <span className="relative">{children}</span>
    </>
  )

  return (
    <span className={cn('relative inline-flex', glow ? 'btn-glow' : undefined)}>
      {anchor ? (
        <a
          href={to}
          className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
          // Only an off-site link opens a new tab. Sending the customer to their own terminal
          // in a second tab leaves the marketing page orphaned behind it.
          {...(offsite ? { rel: 'noreferrer noopener', target: '_blank' } : {})}
          {...rest}
        >
          {inner}
        </a>
      ) : (
        <Link to={to} className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
          {inner}
        </Link>
      )}
    </span>
  )
}
